export class WindowManager {
  constructor(devtools) {
    this.devtools = devtools;
    this.windows = new Map();
    this.zIndexCounter = 1000000;
  }

  createErrorWindow(errorData) {
    const windowId = errorData.id;

    if (this.windows.has(windowId)) {
      this.bringToFront(windowId);
      return;
    }

    const windowElement = this.createWindowElement(errorData);
    document.body.appendChild(windowElement);

    this.windows.set(windowId, {
      element: windowElement,
      data: errorData,
      type: 'error'
    });

    this.makeWindowDraggable(windowElement);
    this.positionWindow(windowElement);
  }

  createWindowElement(errorData) {
    const window = document.createElement('div');
    window.className = 'devtools-window';
    window.style.zIndex = ++this.zIndexCounter;

    window.innerHTML = `
      <div class="devtools-header">
        <div class="devtools-title">🐛 ${errorData.type.toUpperCase()} ERROR</div>
        <div class="devtools-controls">
          <button class="devtools-btn expand-btn">📋</button>
          <button class="devtools-btn close-btn">✕</button>
        </div>
      </div>
      <div class="devtools-content">
        <div class="devtools-timestamp">
          ${new Date(errorData.timestamp).toLocaleTimeString()}
        </div>
        <div style="margin: 8px 0; color: #ff6b6b;">
          <strong>Message:</strong> ${errorData.message}
        </div>
        ${errorData.source ? `<div style="margin: 4px 0; color: #ffa500;"><strong>Source:</strong> ${errorData.source}:${errorData.lineno}:${errorData.colno}</div>` : ''}
        
        <div class="devtools-expandable" onclick="this.classList.toggle('expanded'); this.nextElementSibling.classList.toggle('devtools-collapsed')">
          Code Snippet
        </div>
        <div class="devtools-code devtools-collapsed">
          ${this.renderCodeSnippet(errorData.codeSnippet)}
        </div>
        
        <div class="devtools-expandable" onclick="this.classList.toggle('expanded'); this.nextElementSibling.classList.toggle('devtools-collapsed')">
          Stack Trace
        </div>
        <div class="devtools-code devtools-collapsed">
          ${errorData.stackTrace.join('\n')}
        </div>
      </div>
    `;

    // Add event listeners
    const closeBtn = window.querySelector('.close-btn');
    closeBtn.addEventListener('click', () => this.closeWindow(errorData.id));

    const expandBtn = window.querySelector('.expand-btn');
    expandBtn.addEventListener('click', () => this.toggleWindowSize(window));

    return window;
  }

  createInspectionWindow(errorData) {
    const windowId = `inspect_${errorData.id}`;
    
    if (this.windows.has(windowId)) {
      this.bringToFront(windowId);
      return;
    }

    const windowElement = this.createInspectionWindowElement(errorData);
    document.body.appendChild(windowElement);
    
    this.windows.set(windowId, {
      element: windowElement,
      data: errorData,
      type: 'inspection'
    });

    this.makeWindowDraggable(windowElement);
    this.positionWindow(windowElement);
  }

  createInspectionWindowElement(errorData) {
    const window = document.createElement('div');
    window.className = 'devtools-window';
    window.style.zIndex = ++this.zIndexCounter;
    window.style.width = '700px';
    window.style.maxWidth = '90vw';
    
    const analysis = errorData.analysis || {};
    
    window.innerHTML = `
      <div class="devtools-header">
        <div class="devtools-title">🔍 DEEP INSPECTION - ${errorData.type.toUpperCase()}</div>
        <div class="devtools-controls">
          <button class="devtools-btn close-btn">✕</button>
        </div>
      </div>
      <div class="devtools-content" style="max-height: 600px;">
        <div id="analysis-content-${errorData.id}">
          Loading deep analysis...
        </div>
      </div>
    `;

    // Add close functionality
    const closeBtn = window.querySelector('.close-btn');
    closeBtn.addEventListener('click', () => this.closeWindow(`inspect_${errorData.id}`));

    // Load analysis content
    setTimeout(() => {
      const contentDiv = window.querySelector(`#analysis-content-${errorData.id}`);
      if (this.devtools.tableRenderer && analysis) {
        const analysisTable = this.devtools.tableRenderer.createAnalysisTable(analysis);
        contentDiv.innerHTML = '';
        contentDiv.appendChild(analysisTable);
      } else {
        contentDiv.innerHTML = '<div style="color: #ff6b6b;">Analysis not available</div>';
      }
    }, 100);

    return window;
  }

  createErrorTableWindow() {
    const windowId = 'error_table';
    
    if (this.windows.has(windowId)) {
      this.bringToFront(windowId);
      return;
    }

    const windowElement = this.createErrorTableWindowElement();
    document.body.appendChild(windowElement);
    
    this.windows.set(windowId, {
      element: windowElement,
      data: null,
      type: 'error-table'
    });

    this.makeWindowDraggable(windowElement);
    this.positionWindow(windowElement);
  }

  createErrorTableWindowElement() {
    const window = document.createElement('div');
    window.className = 'devtools-window';
    window.style.zIndex = ++this.zIndexCounter;
    window.style.width = '900px';
    window.style.maxWidth = '95vw';
    
    window.innerHTML = `
      <div class="devtools-header">
        <div class="devtools-title">📊 ERROR DASHBOARD</div>
        <div class="devtools-controls">
          <button class="devtools-btn refresh-btn">🔄</button>
          <button class="devtools-btn close-btn">✕</button>
        </div>
      </div>
      <div class="devtools-content" style="max-height: 500px;">
        <div id="error-table-content">
          Loading error data...
        </div>
      </div>
    `;

    // Add event listeners
    const closeBtn = window.querySelector('.close-btn');
    closeBtn.addEventListener('click', () => this.closeWindow('error_table'));

    const refreshBtn = window.querySelector('.refresh-btn');
    refreshBtn.addEventListener('click', () => this.refreshErrorTable(window));

    // Load initial data
    setTimeout(() => this.refreshErrorTable(window), 100);

    return window;
  }

  refreshErrorTable(windowElement) {
    const contentDiv = windowElement.querySelector('#error-table-content');
    const errors = this.devtools.runtimeManager.errors || [];
    
    if (this.devtools.tableRenderer && errors.length > 0) {
      const errorTable = this.devtools.tableRenderer.createErrorTable(errors);
      contentDiv.innerHTML = '';
      contentDiv.appendChild(errorTable);
    } else {
      contentDiv.innerHTML = '<div style="color: #888; text-align: center; padding: 20px;">No errors captured yet</div>';
    }
  }

  createFixesWindow(errorData) {
    const windowId = `fixes_${errorData.id}`;
    
    if (this.windows.has(windowId)) {
      this.bringToFront(windowId);
      return;
    }

    const windowElement = this.createFixesWindowElement(errorData);
    document.body.appendChild(windowElement);
    
    this.windows.set(windowId, {
      element: windowElement,
      data: errorData,
      type: 'fixes'
    });

    this.makeWindowDraggable(windowElement);
    this.positionWindow(windowElement);
  }

  createFixesWindowElement(errorData) {
    const window = document.createElement('div');
    window.className = 'devtools-window';
    window.style.zIndex = ++this.zIndexCounter;
    window.style.width = '500px';
    
    const suggestions = errorData.analysis?.suggestions || [];
    
    window.innerHTML = `
      <div class="devtools-header">
        <div class="devtools-title">🔧 QUICK FIXES</div>
        <div class="devtools-controls">
          <button class="devtools-btn close-btn">✕</button>
        </div>
      </div>
      <div class="devtools-content">
        ${suggestions.length > 0 ? 
          suggestions.map(suggestion => `
            <div class="devtools-suggestion-item">
              <div class="suggestion-header">
                <span class="suggestion-title">${suggestion.title}</span>
                <span class="suggestion-priority priority-${suggestion.priority}">${suggestion.priority}</span>
              </div>
              <div class="suggestion-description">${suggestion.description}</div>
              <div class="suggestion-code">
                <pre><code>${suggestion.code}</code></pre>
                <button class="copy-btn" onclick="navigator.clipboard.writeText('${suggestion.code.replace(/'/g, "\\'")}')">📋</button>
              </div>
            </div>
          `).join('') :
          '<div style="color: #888; text-align: center; padding: 20px;">No automated fixes available</div>'
        }
      </div>
    `;

    // Add close functionality
    const closeBtn = window.querySelector('.close-btn');
    closeBtn.addEventListener('click', () => this.closeWindow(`fixes_${errorData.id}`));

    return window;
  }

  renderCodeSnippet(snippet) {
    if (!snippet || !snippet.lines) return 'No code snippet available';

    return snippet.lines.map((line, index) => {
      const lineNumber = index + 1;
      const isErrorLine = lineNumber === snippet.errorLine;
      const className = isErrorLine ? 'devtools-error-line' : '';

      return `<div class="${className}">${lineNumber.toString().padStart(3, ' ')} | ${line}</div>`;
    }).join('');
  }

  makeWindowDraggable(windowElement) {
    const header = windowElement.querySelector('.devtools-header');
    let isDragging = false;
    let currentX;
    let currentY;
    let initialX;
    let initialY;

    header.addEventListener('mousedown', (e) => {
      isDragging = true;
      initialX = e.clientX - windowElement.offsetLeft;
      initialY = e.clientY - windowElement.offsetTop;
      this.bringToFront(null, windowElement);
    });

    document.addEventListener('mousemove', (e) => {
      if (isDragging) {
        e.preventDefault();
        currentX = e.clientX - initialX;
        currentY = e.clientY - initialY;

        // Constrain to browser boundaries
        const windowRect = windowElement.getBoundingClientRect();
        const maxX = window.innerWidth - windowRect.width;
        const maxY = window.innerHeight - windowRect.height;

        currentX = Math.max(0, Math.min(currentX, maxX));
        currentY = Math.max(0, Math.min(currentY, maxY));

        windowElement.style.left = currentX + 'px';
        windowElement.style.top = currentY + 'px';
      }
    });

    document.addEventListener('mouseup', () => {
      isDragging = false;
    });
  }

  positionWindow(windowElement) {
    const windowCount = this.windows.size;
    const offset = (windowCount - 1) * 30;

    windowElement.style.left = (100 + offset) + 'px';
    windowElement.style.top = (100 + offset) + 'px';
  }

  bringToFront(windowId, windowElement = null) {
    const element = windowElement || this.windows.get(windowId)?.element;
    if (element) {
      element.style.zIndex = ++this.zIndexCounter;
    }
  }

  toggleWindowSize(windowElement) {
    const content = windowElement.querySelector('.devtools-content');
    const isExpanded = content.style.maxHeight === 'none';

    if (isExpanded) {
      content.style.maxHeight = '400px';
      windowElement.style.maxWidth = '800px';
    } else {
      content.style.maxHeight = 'none';
      windowElement.style.maxWidth = '90vw';
    }
  }

  closeWindow(windowId) {
    const window = this.windows.get(windowId);
    if (window) {
      window.element.remove();
      this.windows.delete(windowId);
    }
  }

  closeAll() {
    this.windows.forEach((window, id) => {
      this.closeWindow(id);
    });
  }
}